import { FiArrowUpRight } from "react-icons/fi";
import { useInView } from "../../hooks/useInView";
import { siteConfig } from "../../config/site";
import travelPhoto from "../../assets/about-us-page/hero-1.png";
import emergencyPhoto from "../../assets/about-us-page/hero-2.png";
import dealsPhoto from "../../assets/about-us-page/hero-3.png";
import userIcon from "../../assets/all-icons/user-profile-white.png";
import buildingIcon from "../../assets/all-icons/building-white.png";
import lightningIcon from "../../assets/all-icons/lightning-bolt-white.png";
import checkmarkIcon from "../../assets/all-icons/checkmark-circle-teal.png";

interface CategoryCard {
  key: string;
  icon: string;
  iconBg: string;
  title: string;
  subtitle: string;
  photo: string;
  photoAlt: string;
  features: string[];
  caption: string;
  ctaLabel: string;
  ctaBg: string;
}

const CARDS: CategoryCard[] = [
  {
    key: "travel",
    icon: userIcon,
    iconBg: "#3e025c",
    title: "Loan for Travel",
    subtitle: "Explore the world with easy financing.",
    photo: travelPhoto,
    photoAlt: "Traveler with luggage watching a plane take off",
    features: ["Flights & hotels", "Holiday packages", "Travel insurance", "And more"],
    caption: "Affordable travel loans to fund your dream getaway.",
    ctaLabel: "Explore",
    ctaBg: "#281537",
  },
  {
    key: "emergency",
    icon: buildingIcon,
    iconBg: "#176766",
    title: "Loan for Emergency Needs",
    subtitle: "Fast funds when you need them most.",
    photo: emergencyPhoto,
    photoAlt: "Nurse and doctor attending to a patient in a hospital bed",
    features: ["Medical expenses", "Urgent repairs", "Unexpected bills", "And more"],
    caption: "Quick disbursement to help you handle life's surprises.",
    ctaLabel: "Explore",
    ctaBg: "#176766",
  },
  {
    key: "deals",
    icon: lightningIcon,
    iconBg: "#3e025c",
    title: "Loan for Amazing Deals",
    subtitle: "Don't miss out on great offers.",
    photo: dealsPhoto,
    photoAlt: "Person relaxing with a phone surrounded by electronics and shopping icons",
    features: ["Electronics & gadgets", "Home appliances", "Seasonal sales", "And more"],
    caption: "Grab the best deals with flexible personal financing.",
    ctaLabel: "Check Eligibility",
    ctaBg: "#281537",
  },
];

export function CategoryCards() {
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <section className="mx-auto w-full max-w-[1280px] px-4 pb-16 pt-8 sm:pb-20 sm:pt-10">
      <div
        ref={ref}
        className={`grid grid-cols-1 gap-8 md:grid-cols-3 ${inView ? "in-view" : ""}`}
      >
        {CARDS.map((card, idx) => (
          <div
            key={card.key}
            className="flex flex-col rounded-[24px] border border-solid border-[#e7dce8] bg-white p-[29px] shadow-[0px_4px_12px_rgba(62,2,92,0.08)]"
            style={{ animationDelay: `${idx * 0.15}s` }}
          >
            {/* Icon row */}
            <div className="flex items-center justify-between">
              <span
                className="flex h-10 w-10 items-center justify-center rounded-xl"
                style={{ backgroundColor: card.iconBg }}
              >
                <img src={card.icon} alt="" className="h-5 w-5" />
              </span>
              <FiArrowUpRight className="h-4 w-4 text-[#1c1917]/60" />
            </div>

            {/* Title + subtitle */}
            <h3 className="reveal-scroll delay-1 pt-6 text-xl font-bold leading-7 text-[#1c1917]">
              {card.title}
            </h3>
            <p className="reveal-scroll delay-2 pt-1 text-sm text-[#64748b]">
              {card.subtitle}
            </p>

            {/* Photo */}
            <div className="mt-5 h-[176px] w-full overflow-hidden rounded-2xl">
              <img
                src={card.photo}
                alt={card.photoAlt}
                className="h-full w-full object-cover"
              />
            </div>

            {/* Feature checklist */}
            <ul className="reveal-scroll delay-3 mt-6 flex flex-col gap-[10px]">
              {card.features.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <img src={checkmarkIcon} alt="" className="h-4 w-4 shrink-0" />
                  <span className="text-sm font-medium text-[#374151]">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            {/* Caption */}
            <p className="reveal-scroll delay-4 mt-6 text-sm text-[#64748b]">
              {card.caption}
            </p>

            {/* CTA */}
            <a
              href={siteConfig.applyLink || undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 flex w-fit items-center gap-2 rounded-full px-6 py-[10px] text-sm font-semibold text-white shadow-[0px_1px_1px_rgba(0,0,0,0.05)] transition-opacity hover:opacity-90"
              style={{ backgroundColor: card.ctaBg }}
            >
              {card.ctaLabel}
              <span aria-hidden="true">→</span>
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}
